import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslate } from '../../mixins/translate/translate';
import { openApiFetchDelete, openApiFetchGet, openApiFetchPost } from '../../store/openApiFetch';
import { IconRemoveButton } from '../common/iconButtons/IconRemoveButton';
import {
    Content,
    Panel,
    PanelBody,
    PanelTable,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    TopbarCrumbActive,
    TopbarLeftOnly,
    Tray,
} from '../elements';
import { Spinner } from '../spinners';
import { useReactAsync } from '../utils/useReactAsync';

interface IQueueSummary {
    all: number;
    created: number;
    active: number;
    completed: number;
    failed: number;
    cancelled: number;
}

function getStateBadgeStyle(state: string): React.CSSProperties {
    const base: React.CSSProperties = {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '3px',
        fontSize: '12px',
        fontWeight: 600,
        color: '#fff',
    };

    switch (state) {
        case 'created':
        case 'retry':
            return { ...base, backgroundColor: '#6c757d' };
        case 'active':
            return { ...base, backgroundColor: '#007bff' };
        case 'completed':
            return { ...base, backgroundColor: '#28a745' };
        case 'failed':
            return { ...base, backgroundColor: '#dc3545' };
        case 'cancelled':
            return { ...base, backgroundColor: '#ffc107', color: '#333' };
        case 'expired':
            return { ...base, backgroundColor: '#868e96' };
        default:
            return { ...base, backgroundColor: '#6c757d' };
    }
}

async function loadJobs() {
    return await openApiFetchGet('/api/ai/queue/jobs', {});
}

async function loadSummary(): Promise<IQueueSummary> {
    return await openApiFetchGet('/api/ai/queue/summary', {});
}

function SummaryCards({ summary }: { summary: IQueueSummary }) {
    const cardStyle: React.CSSProperties = {
        display: 'inline-block',
        padding: '8px 16px',
        marginRight: '10px',
        marginBottom: '10px',
        borderRadius: '4px',
        backgroundColor: '#f5f5f5',
        border: '1px solid #ddd',
        textAlign: 'center',
        minWidth: '80px',
    };

    return (
        <div style={{ padding: '15px' }}>
            <div style={cardStyle}>
                <div style={{ fontSize: '20px', fontWeight: 700 }}>{summary.all}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Total</div>
            </div>
            <div style={{ ...cardStyle, borderColor: '#6c757d' }}>
                <div style={{ fontSize: '20px', fontWeight: 700 }}>{summary.created}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Queued</div>
            </div>
            <div style={{ ...cardStyle, borderColor: '#007bff' }}>
                <div style={{ fontSize: '20px', fontWeight: 700 }}>{summary.active}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Active</div>
            </div>
            <div style={{ ...cardStyle, borderColor: '#28a745' }}>
                <div style={{ fontSize: '20px', fontWeight: 700 }}>{summary.completed}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Completed</div>
            </div>
            <div style={{ ...cardStyle, borderColor: '#dc3545' }}>
                <div style={{ fontSize: '20px', fontWeight: 700 }}>{summary.failed}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Failed</div>
            </div>
        </div>
    );
}

function JobOutput({ output, isFailed }: { output: Record<string, unknown> | null; isFailed: boolean }) {
    if (!output) {
        return <>-</>;
    }
    const text = JSON.stringify(output);
    return (
        <span title={JSON.stringify(output, undefined, 2)} style={{ fontSize: '12px', color: isFailed ? '#dc3545' : '#333' }}>
            {text.substring(0, 100)}
            {text.length > 100 ? '...' : ''}
        </span>
    );
}

function formatDate(dateStr: string | null): string {
    if (!dateStr) {
        return '-';
    }
    try {
        const date = new Date(dateStr);
        return date.toLocaleString('nl-NL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    } catch {
        return dateStr;
    }
}

export function EmbedQueue() {
    const translate = useTranslate();
    const { data: jobsData, reload: reloadJobs, isLoading: isLoadingJobs } = useReactAsync(loadJobs, {}, []);
    const { data: summary, reload: reloadSummary } = useReactAsync(loadSummary, {}, []);

    // Auto-refresh every 10 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            void reloadJobs();
            void reloadSummary();
        }, 10000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function cancelJob(jobId: string) {
        await openApiFetchDelete('/api/ai/queue/jobs/{jobId}', { jobId });
        await reloadJobs();
        await reloadSummary();
    }

    async function resubmitJob(jobId: string) {
        await openApiFetchPost('/api/ai/queue/jobs/{jobId}/resubmit', { jobId }, undefined);
        await reloadJobs();
        await reloadSummary();
    }

    return (
        <>
            <TopbarLeftOnly>
                <TopbarCrumbActive title="Embed Queue" icon="fa-database" />
            </TopbarLeftOnly>
            <Content>
                <Tray>
                    <Panel>{summary && <SummaryCards summary={summary} />}</Panel>
                    {(isLoadingJobs && <Spinner />) || (
                        <PanelBody noPadding={true}>
                            <PanelTable>
                                <TableHead>
                                    <TableHeadRow>
                                        <TableHeader>Status</TableHeader>
                                        <TableHeader>File</TableHeader>
                                        <TableHeader>Created</TableHeader>
                                        <TableHeader>Started</TableHeader>
                                        <TableHeader>Completed</TableHeader>
                                        <TableHeader>Retries</TableHeader>
                                        <TableHeader>Output</TableHeader>
                                        <TableHeader>&nbsp;</TableHeader>
                                    </TableHeadRow>
                                </TableHead>
                                <TableBody>
                                    {jobsData?.items?.map((job) => {
                                        const sourceType = job.data.sourceType ?? 'asset';
                                        const sourceId = job.data.sourceId ?? job.data.assetId ?? '';
                                        return (
                                            <TableRow key={job.id}>
                                                <TableData>
                                                    <span style={getStateBadgeStyle(job.state)}>{job.state}</span>
                                                </TableData>
                                                <TableData>
                                                    {sourceType === 'survey' ? (
                                                        <Link
                                                            to={`/surveys/${sourceId}`}
                                                            title={`${sourceType}:${sourceId}`}
                                                            style={{ fontSize: '12px' }}
                                                        >
                                                            [{sourceType}] {job.assetName ?? `${sourceId.substring(0, 8)}..`}
                                                        </Link>
                                                    ) : (
                                                        <span title={`${sourceType}:${sourceId}`} style={{ fontSize: '12px' }}>
                                                            [{sourceType}] {job.assetName ?? `${sourceId.substring(0, 8)}..`}
                                                        </span>
                                                    )}
                                                </TableData>
                                                <TableData>{formatDate(job.createdOn)}</TableData>
                                                <TableData>{formatDate(job.startedOn)}</TableData>
                                                <TableData>{formatDate(job.completedOn)}</TableData>
                                                <TableData>
                                                    {job.retryCount}/{job.retryLimit}
                                                </TableData>
                                                <TableData>
                                                    {job.output ? <JobOutput output={job.output} isFailed={job.state === 'failed'} /> : '-'}
                                                </TableData>
                                                <TableData alignRight={true}>
                                                    {(job.state === 'failed' || job.state === 'expired') &&
                                                        !jobsData?.items?.some((other) => {
                                                            const otherId = other.data.sourceId ?? other.data.assetId;
                                                            return (
                                                                otherId === sourceId &&
                                                                (other.state === 'created' || other.state === 'retry' || other.state === 'active')
                                                            );
                                                        }) && (
                                                            <button
                                                                title="Resubmit"
                                                                className="btn btn-xs btn-default"
                                                                onClick={async () => resubmitJob(job.id)}
                                                            >
                                                                <i className="fa fa-refresh" />
                                                            </button>
                                                        )}
                                                    {(job.state === 'created' || job.state === 'retry' || job.state === 'active') && (
                                                        <IconRemoveButton
                                                            removeQuestion={translate('common.confirmDeleteItem')}
                                                            onClick={async () => cancelJob(job.id)}
                                                        />
                                                    )}
                                                </TableData>
                                            </TableRow>
                                        );
                                    })}
                                    {(!jobsData?.items || jobsData.items.length === 0) && (
                                        <TableRow>
                                            <TableData>No jobs found</TableData>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </PanelTable>
                        </PanelBody>
                    )}
                </Tray>
            </Content>
        </>
    );
}
